import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import type { IMonitorReplay } from '@interfaces/IMonitorApi';
import { dayScale, siteClock } from './trackScale';
import styles from './ReplayTrack.module.css';

/** The site's shift for the replayed weekday, as instants. */
export interface IReplayShift {
  startMs: number;
  endMs: number;
}

/** SVG viewBox height and the curve's top/bottom in it. */
const CURVE_BASE = 38;
const CURVE_RANGE = 34;
/** A line label past this share of the track flips to the line's left side. */
const FLIP_PCT = 80;

interface IPoint {
  x: number;
  y: number;
}

/** The headcount curve and its area as SVG paths. Across a data gap the count
 *  holds at its last value, then steps to the frame that ends the gap; after
 *  the last frame it holds to the data end (the day's end, or now). Before the
 *  first frame nothing is drawn: no data. */
function curvePaths(replay: IMonitorReplay, scale: ReturnType<typeof dayScale>) {
  const max = Math.max(1, ...replay.frames.map((f) => f.workers));
  const gapEnds = new Set(replay.gaps.map((g) => Date.parse(g.to)));
  const points: IPoint[] = [];
  for (const f of replay.frames) {
    const ms = Date.parse(f.t);
    if (ms > scale.dataEnd) continue;
    const point = { x: scale.percentAt(ms), y: CURVE_BASE - (f.workers / max) * CURVE_RANGE };
    const last = points.at(-1);
    if (last && gapEnds.has(ms)) points.push({ x: point.x, y: last.y });
    points.push(point);
  }
  const last = points.at(-1);
  if (last) points.push({ x: scale.percentAt(scale.dataEnd), y: last.y });
  const lineTo = points.map((point) => `L${point.x},${point.y}`).join(' ');
  const xs = points.map((point) => point.x);
  return {
    line: `M${lineTo.slice(1)}`,
    area: `M${xs[0]},${CURVE_BASE} ${lineTo} L${xs[xs.length - 1]},${CURVE_BASE} Z`,
    /** Where the first frame sits: no data before it. */
    firstX: Math.min(...xs),
  };
}

interface ITrackDayProps {
  replay: IMonitorReplay;
  tz: string;
  shift: IReplayShift | null;
}

/** Everything that depends on the day only — curve, hatch, ticks, framing lines — memoized so a tick skips it. */
export const TrackDay = memo(function TrackDay({ replay, tz, shift }: ITrackDayProps) {
  const { t } = useTranslation();
  const scale = dayScale(replay);
  const { start, end, dataEnd, percentAt } = scale;
  const finished = dataEnd >= end;
  const { line, area, firstX } = curvePaths(replay, scale);

  const ticks: { ms: number; label: string }[] = [];
  // The first whole hour at or after the start, then onto an even hour.
  let tick = DateTime.fromMillis(start - 1, { zone: tz }).startOf('hour').plus({ hours: 1 });
  tick = tick.plus({ hours: tick.hour % 2 });
  for (; tick.toMillis() <= end; tick = tick.plus({ hours: 2 })) ticks.push({ ms: tick.toMillis(), label: tick.toFormat('HH:mm') });

  const lines = [
    replay.firstIn && { ms: Date.parse(replay.firstIn), key: 'firstIn', at: 'top' },
    finished && replay.lastOut && { ms: Date.parse(replay.lastOut), key: 'lastOut', at: 'top' },
    shift && { ms: shift.startMs, key: 'shiftStart', at: 'bottom' },
    shift && { ms: shift.endMs, key: 'shiftEnd', at: 'bottom' },
  ].filter((l): l is { ms: number; key: string; at: string } => !!l && l.ms >= start && l.ms <= dataEnd);

  return (
    <>
      {replay.firstIn ? (
        <svg className={styles.curve} viewBox="0 0 100 40" preserveAspectRatio="none" data-testid="replay-curve" aria-hidden>
          <path className={styles.area} d={area} />
          <path className={styles.line} d={line} />
          {/* Before the day's first snapshot there is no data yet: a dotted line along the baseline. */}
          {firstX > 0 && (
            <line className={styles.noDataLine} x1={0} x2={firstX} y1={CURVE_BASE - 1} y2={CURVE_BASE - 1} data-testid="replay-no-data" />
          )}
        </svg>
      ) : (
        <span className={styles.empty}>{t('monitor.replay.empty')}</span>
      )}
      {!finished && <div className={styles.future} style={{ left: `${percentAt(dataEnd)}%` }} data-testid="replay-future" />}
      <div className={styles.ticks} aria-hidden>
        {ticks.map((tk) => (
          <span key={tk.ms} style={{ left: `${percentAt(tk.ms)}%` }} data-testid="replay-tick">
            {tk.label}
          </span>
        ))}
      </div>
      {lines.map((l) => (
        <div
          key={l.key}
          className={styles.dashed}
          data-at={l.at}
          data-flip={percentAt(l.ms) > FLIP_PCT}
          style={{ left: `${percentAt(l.ms)}%` }}
        >
          <i>{t(`monitor.replay.${l.key}`, { time: siteClock(l.ms, tz) })}</i>
        </div>
      ))}
    </>
  );
});
